import { useMutation, useQueryClient } from '@tanstack/react-query'
import { GridApi } from 'ag-grid-community'
import { FormInstance, message } from 'antd'
import { useCallback } from 'react'
import { invalidationUtils } from '../../../../api/queryClient'
import { fetchClient } from '../../../../api/typedClient'
import { CreateFormData, UpdateFormData } from '../types'
import {
  convertFormDataToPayload,
  getEntityEndpoint,
  getEntityId,
} from '../utils'

interface UseMasterDataCRUDProps {
  entityType: string
  gridApi: GridApi | null
}

interface UseMasterDataCRUDReturn {
  createMutation: ReturnType<typeof useMutation>
  updateMutation: ReturnType<typeof useMutation>
  handleCreate: (formData: CreateFormData) => Promise<void>
  handleUpdate: (updatedRow: UpdateFormData) => Promise<void>
  handleCreateSubmit: (form: FormInstance) => Promise<boolean>
}

/**
 * Hook for managing CRUD operations in MasterDataGrid
 */
export const useMasterDataCRUD = ({
  entityType,
  gridApi,
}: UseMasterDataCRUDProps): UseMasterDataCRUDReturn => {
  const queryClient = useQueryClient()

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (formData: CreateFormData) => {
      const endpoint = getEntityEndpoint(entityType)
      const { data, error } = await fetchClient.POST(
        // @ts-expect-error TODO: Dynamic entity endpoints not in OpenAPI spec
        endpoint,
        {
          body: formData,
          headers: {
            'X-Tenant-Name': 'Gravitate', // Master data uses Gravitate tenant
          },
        },
      )
      if (error) {
        throw error
      }
      return data
    },
    onSuccess: () => {
      message.success(`${entityType} created successfully`)
      // Refresh grid
      if (gridApi) {
        gridApi.refreshServerSide({ purge: true })
      }
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ['crossroads-entity-grid', entityType],
      })
      invalidationUtils.smartInvalidate('createMasterData')
    },
    onError: (error: unknown) => {
      console.error('Create error:', error)

      // Check if it's a 422 validation error
      type ValidationDetail = { loc?: unknown[]; msg?: string }
      type ErrShape = {
        response?: {
          status?: number
          data?: { detail?: ValidationDetail[] }
        }
        message?: string
      }
      const err = error as ErrShape
      if (err.response?.status === 422 && err.response?.data?.detail) {
        const validationErrors = err.response.data.detail

        // Convert validation errors to form field errors
        const fieldErrors = validationErrors.map((errItem) => {
          // Extract field name from location array (e.g., ["body", "address"] -> "address")
          const loc = Array.isArray(errItem.loc) ? errItem.loc : []
          const fieldName =
            (loc[loc.length - 1] as string) || (loc[0] as string)

          return {
            name: fieldName,
            errors: [errItem.msg || 'Field required'],
          }
        })

        // Return field errors for form handling
        throw { fieldErrors, message: 'Please fill in all required fields' }
      } else {
        // Handle other types of errors
        const detail =
          (err.response?.data as { detail?: string })?.detail || err.message
        const errorMessage = `Failed to create ${entityType}: ${detail || 'Unknown error'}`
        message.error(errorMessage)
        throw error
      }
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (updatedRow: UpdateFormData) => {
      const id = getEntityId(updatedRow, entityType)
      if (!id) {
        throw new Error(
          `Entity ID is missing for ${entityType}. Available fields: ${Object.keys(updatedRow).join(', ')}`,
        )
      }

      const endpoint = getEntityEndpoint(entityType)
      const { data, error } = await fetchClient.PUT(
        // @ts-expect-error TODO: Dynamic entity endpoints not in OpenAPI spec
        `${endpoint}${id}`,
        {
          body: updatedRow,
          headers: {
            'X-Tenant-Name': 'Gravitate', // Master data uses Gravitate tenant
          },
        },
      )
      if (error) {
        throw error
      }
      return data
    },
    onSuccess: () => {
      message.success(`${entityType} updated successfully`)
      // Refresh server-side grid
      if (gridApi) {
        gridApi.refreshServerSide({ route: [], purge: false })
      }
    },
    onError: (error: unknown) => {
      const err = error as { message?: string }
      message.error(
        `Failed to update ${entityType}: ${err.message || 'Unknown error'}`,
      )
    },
  })

  // Handle create operation
  const handleCreate = useCallback(
    async (formData: CreateFormData) => {
      const payload = convertFormDataToPayload(formData)
      await createMutation.mutateAsync(payload)
    },
    [createMutation],
  )

  // Handle update operation
  const handleUpdate = useCallback(
    async (updatedRow: UpdateFormData) => {
      try {
        await updateMutation.mutateAsync(updatedRow)
      } catch (error) {
        console.error('❌ Update failed:', error)
        throw error
      }
    },
    [updateMutation],
  )

  // Handle form submission for create
  const handleCreateSubmit = useCallback(
    async (form: FormInstance) => {
      try {
        const values = await form.validateFields()
        const formData = convertFormDataToPayload(values)
        await createMutation.mutateAsync(formData)
        form.resetFields()
        return true // success
      } catch (error: unknown) {
        const err = error as { fieldErrors?: unknown; message?: string }
        if (err.fieldErrors) {
          // Set field errors on the form and keep drawer open
          form.setFields(
            err.fieldErrors as unknown as Parameters<typeof form.setFields>[0],
          )
          message.error(err.message)
          return false // validation error (e.g., 422)
        } else {
          console.error('Form validation failed:', error)
          return false // other errors or client-side validation failed
        }
      }
    },
    [createMutation],
  )

  return {
    createMutation,
    updateMutation,
    handleCreate,
    handleUpdate,
    handleCreateSubmit,
  }
}
